import { advancedReadCursor } from './openAtUnread';
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../platform/auth/AuthContext';
import { MessagingService } from '../../services/MessagingService';
import type { MessagingRealtimeStatus } from '../../services/MessagingService';
import { DataService } from '../../services/DataService';
import { Chat, isClientTeamChat } from '../../types/messaging';
import { toast } from 'sonner';
import { NATIVE_REFRESH_EVENT, NativeBridge } from '../../services/NativeBridge';
import { getChatMetadata, getMessageNotificationPreview, formatRelativeTime, isSameUser } from './utils';
import type { ChatMetadata } from './utils';
import { isNativeApp } from '../../utils/nativeApp';
import { MessageNotificationService, visibleNotificationPreview } from '../../services/MessageNotificationService';
import { getPreferencesSnapshot } from '../preferences/preferencesStore';
import { AuthenticatedAttachmentService } from '../../services/AuthenticatedAttachmentService';
import { warmChatPages } from './messagePageCache';
import { backgroundWarmAllowed, whenIdle } from '../../services/mainScreenWarm';

/**
 * Messaging Context - Provides chat list and unread count
 */
interface MessagingContextType {
  unreadCount: number;
  chats: Chat[];
  isLoadingChats: boolean;
  chatsUnavailable: boolean;
  realtimeStatus: MessagingRealtimeStatus;
  refreshChats: () => Promise<void>;
  markChatAsRead: (chatId: string, observedMessageId?: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<boolean>;
  hardDeleteChat: (chatId: string) => Promise<boolean>;
  /** "Delete for me" — hides the chat from this user's list only. */
  hideChat: (chatId: string) => Promise<boolean>;
  /** iOS Mail-style toggle — flags the chat unread for this user only. */
  markChatAsUnread: (chatId: string) => Promise<boolean>;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

const FOCUS_RESYNC_GAP_MS = 60_000;

export function MessagingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const activeUserIdRef=useRef(user?.id);activeUserIdRef.current=user?.id;
  const actorEpoch=useRef({id:user?.id,generation:0});
  if(actorEpoch.current.id!==user?.id)actorEpoch.current={id:user?.id,generation:actorEpoch.current.generation+1};
  const generation=actorEpoch.current.generation;
  const [unreadCount, setUnreadCount] = useState(0);
  const [chats, setChats] = useState<Chat[]>([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [chatsUnavailable, setChatsUnavailable] = useState(false);
  const chatsLoadedRef = useRef(false);
  const lastChatsLoadAt = useRef(0);
  const loadedUserIdRef = useRef<string | null>(null);
  const seenMessageIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    AuthenticatedAttachmentService.clearForSessionChange();
  }, [user?.id]);

  // Note: browser notification permission is handled by useBrowserPermissionsBootstrap
  // in App.tsx which shows the branded PermissionPrompt before triggering the native
  // browser dialog (requires a real user gesture — can't be done silently here).

  // Centralized Chat Loading
  const loadChats = useCallback(async (force = false) => {
    if (!user) {
        setChats([]);
        setUnreadCount(0);
        setChatsUnavailable(false);
        chatsLoadedRef.current = false;
        loadedUserIdRef.current = null;
        seenMessageIdsRef.current.clear();
        return;
    }

    if (loadedUserIdRef.current !== user.id) {
      chatsLoadedRef.current = false;
      loadedUserIdRef.current = user.id;
      seenMessageIdsRef.current.clear();
    }

    // Prevent duplicate initial loads if not forced (use ref to avoid stale closure)
    if (!force && chatsLoadedRef.current) return;

    try {
        if (!chatsLoadedRef.current) setIsLoadingChats(true);
        const fetchedChats = await MessagingService.getChats(user.id);
        if(activeUserIdRef.current!==user.id||actorEpoch.current.generation!==generation)return;
        setChats(fetchedChats);
        setChatsUnavailable(false);
        // The chats people open first are ready before Messages is.
        const warmedFor = user.id;
        whenIdle(() => {
          void warmChatPages(warmedFor, fetchedChats.slice(0, 5).map((chat) => chat.id), {
            concurrency: 2,
            isCurrent: () => activeUserIdRef.current === warmedFor && backgroundWarmAllowed(),
          });
        });
        
        // Calculate unread
        const count = fetchedChats.reduce((acc, chat) => acc + (chat.unreadCount || 0), 0);
        setUnreadCount(count);
        
        chatsLoadedRef.current = true;
        lastChatsLoadAt.current = Date.now();
    } catch (error) {
        console.error('Error loading chats:', error);
        setChatsUnavailable(true);
    } finally {
        setIsLoadingChats(false);
    }
  }, [user]);

  // Mark as read helper
  const markChatAsRead = useCallback(async (chatId: string, observedMessageId?: string) => {
      if (!user || activeUserIdRef.current!==user.id) return;

      // A viewing read is bounded by its displayed source. Never optimistically
      // clear newer unseen rows or advance the local cursor to the device clock.
      if (observedMessageId !== undefined) {
        try { await MessagingService.markAsRead(chatId, user.id, observedMessageId); }
        catch (error) { console.error('[MessagingContext] Failed to mark observed message read:', error);throw error; }
        if(activeUserIdRef.current===user.id&&actorEpoch.current.generation===generation)await loadChats(true);
        return;
      }
      // Optimistic update — derive the unread delta inside setChats so we
      // always read the current state and avoid a stale-closure on `chats`.
      setChats(prev => {
          const chat = prev.find(c => c.id === chatId);
          const delta = chat?.unreadCount || 0;
          setUnreadCount(u => Math.max(0, u - delta));
          // Advance the local cursor with the count, or reopening the chat
          // before the next list load would treat read messages as unread.
          const readAt = new Date().toISOString();
          return prev.map(c => c.id === chatId
            ? { ...c, unreadCount: 0, last_read_at: advancedReadCursor(c, readAt) }
            : c);
      });

      try {
        await MessagingService.markAsRead(chatId, user.id);
      } catch (error) {
        console.error('[MessagingContext] Failed to mark chat as read:', error);
        toast.error('Could not update the read status.');
        await loadChats(true);
      }
  }, [user, loadChats]);

  // Hard-delete chat helper (admin only — removes for all participants)
  const hardDeleteChat = useCallback(async (chatId: string): Promise<boolean> => {
    if (!user) return false;
    // super_admin ONLY (matches the menu gate + the server force-delete gate).
    // Was `!== 'admin'`, which rejected super_admin (super_admin !== 'admin'),
    // so the delete silently no-op'd for the only role allowed to see it.
    if (user.role !== 'super_admin') {
      toast.error('Only super admins can delete conversations for everyone.');
      return false;
    }
    setChats(prev => {
      const chatToDelete = prev.find(c => c.id === chatId);
      const unreadAmount = chatToDelete?.unreadCount || 0;
      setUnreadCount(u => Math.max(0, u - unreadAmount));
      return prev.filter(c => c.id !== chatId);
    });
    const success = await MessagingService.hardDeleteChat(chatId, user.id);
    if (!success) {
      toast.error('Failed to delete conversation. Please try again.');
      loadChats(true);
      return false;
    }
    await loadChats(true);
    toast.success('Conversation permanently deleted');
    return true;
  }, [user, loadChats]);

  // Leave chat helper (removes current user as participant)
  const deleteChat = useCallback(async (chatId: string): Promise<boolean> => {
    if (!user) {
      return false;
    }

    // Optimistic update — read the current unread delta inside setChats to
    // avoid capturing a stale `chats` snapshot in the closure.
    setChats(prev => {
      const chatToDelete = prev.find(c => c.id === chatId);
      const unreadAmount = chatToDelete?.unreadCount || 0;
      setUnreadCount(u => Math.max(0, u - unreadAmount));
      return prev.filter(c => c.id !== chatId);
    });

    const success = await MessagingService.deleteChat(chatId, user.id);

    if (!success) {
      // Rollback on failure
      toast.error('Failed to delete chat. Please try again.');
      loadChats(true);
      return false;
    } else {
      toast.success('Conversation removed');
      return true;
    }
  }, [user, loadChats]);

  // Hide chat helper ("delete for me") — removes the conversation from THIS
  // user's list only. Membership, messages, and every other participant are
  // untouched. A new incoming message resurfaces it, while the retained
  // hidden_at timestamp keeps the user's pre-deletion history invisible.
  const hideChat = useCallback(async (chatId: string): Promise<boolean> => {
    if (!user) return false;

    // Optimistic removal — same stale-closure-safe pattern as deleteChat.
    setChats(prev => {
      const chatToHide = prev.find(c => c.id === chatId);
      const unreadAmount = chatToHide?.unreadCount || 0;
      setUnreadCount(u => Math.max(0, u - unreadAmount));
      return prev.filter(c => c.id !== chatId);
    });

    const success = await MessagingService.hideChat(chatId, user.id);

    if (!success) {
      toast.error('Failed to remove the conversation. Please try again.');
      loadChats(true);
      return false;
    }
    return true;
  }, [user, loadChats]);

  // Mark-as-unread helper (iOS Mail toggle) — per-user flag; the next
  // markChatAsRead (or opening the chat) clears it server-side.
  const markChatAsUnread = useCallback(async (chatId: string): Promise<boolean> => {
    if (!user) return false;

    // Optimistic: surface the unread state immediately.
    setChats(prev => {
      const chat = prev.find(c => c.id === chatId);
      if (chat && (chat.unreadCount || 0) === 0) {
        setUnreadCount(u => u + 1);
      }
      return prev.map(c => c.id === chatId ? { ...c, unreadCount: Math.max(1, c.unreadCount || 0) } : c);
    });

    const success = await MessagingService.markChatAsUnread(chatId, user.id);
    if (!success) {
      toast.error('Failed to mark as unread. Please try again.');
      loadChats(true);
      return false;
    }
    return true;
  }, [user, loadChats]);

  const refreshChats = useCallback(() => loadChats(true), [loadChats]);
  const [realtimeStatus, setRealtimeStatus] = useState<MessagingRealtimeStatus>('connecting');

  // Initial Load & Subscriptions
  useEffect(() => {
    if (!user) return;

    // Demo session: don't open the global messages realtime channel — the
    // backend `messages` table is shared and would stream other users'
    // chats into the demo. The fetch gate already short-circuits the
    // initial chat-list load.
    if (DataService.isDemoMode()) {
      setChats([]);
      return;
    }

    // Chat badges are global background data. Give the active route a short
    // head start so chat/list does not compete with its critical requests.
    // The Messages route keeps an immediate load because the list is its UI.
    const initialLoadTimer = setTimeout(
      () => loadChats(),
      window.location.pathname.startsWith('/messages') ? 0 : 700,
    );

    // Subscribe to Global Changes
    const channel = MessagingService.subscribeToGlobalChanges(
        user.id,
        (newMsg) => {
            if (seenMessageIdsRef.current.has(newMsg.id)) return;
            seenMessageIdsRef.current.add(newMsg.id);
            // Keep this reconnect-dedupe cache bounded for long-running tabs.
            if (seenMessageIdsRef.current.size > 500) {
              const oldest = seenMessageIdsRef.current.values().next().value;
              if (oldest) seenMessageIdsRef.current.delete(oldest);
            }

            // Skip own messages for notifications
            const isMyMessage = isSameUser(newMsg.sender_id, user.id);
            const isOpenChat = MessagingService.isChatActivelyViewed(newMsg.chat_id);

            setChats(prev => {
                const chatExists = prev.find(c => c.id === newMsg.chat_id);

                if (chatExists) {
                    // Update existing chat
                    const updatedChats = prev.map(c => {
                        if (c.id === newMsg.chat_id) {
                            return {
                                ...c,
                                // Attach isMe so the chat-list "You:" prefix renders correctly
                                lastMessage: { ...newMsg, isMe: isMyMessage },
                                last_message_id: newMsg.id,
                                unreadCount: (!isMyMessage && !isOpenChat && c.lastMessage?.id !== newMsg.id)
                                  ? (c.unreadCount || 0) + 1
                                  : (c.unreadCount || 0),
                                updated_at: newMsg.created_at
                            };
                        }
                        return c;
                    });

                    // Update global unread count
                    const count = updatedChats.reduce((acc, chat) => acc + (chat.unreadCount || 0), 0);
                    setUnreadCount(count);

                    // Sound + browser/native notification for incoming messages
                    if (!isMyMessage && !isOpenChat) {
                        const chat = prev.find(c => c.id === newMsg.chat_id);
                        // Name the sender's own participant row first: in a group the
                        // chat's display name is the group, not the person who wrote.
                        const senderParticipant = chat?.participants?.find(p => isSameUser(p.user_id, newMsg.sender_id))?.user;
                        // getChatMetadata needs the user object; given only the id it
                        // cannot filter out the viewer and names them as the sender.
                        const meta = chat ? getChatMetadata(chat, user) : null;
                        const senderName = senderParticipant?.name
                            || meta?.name
                            || 'New message';
                        const preview = visibleNotificationPreview(
                            getMessageNotificationPreview(newMsg),
                            getPreferencesSnapshot().notifications.showPreview,
                        );

                        // In-app toast — shows regardless of browser notification permission
                        toast(senderName, {
                            description: preview,
                            duration: 5000,
                            action: newMsg.chat_id ? {
                                label: 'Open',
                                onClick: () => {
                                    if (typeof window.__QUANTIS_OPEN_CHAT__ === 'function') {
                                        window.__QUANTIS_OPEN_CHAT__(newMsg.chat_id);
                                    }
                                },
                            } : undefined,
                        });

                        MessageNotificationService.notify({
                            senderName,
                            preview,
                            chatId: newMsg.chat_id,
                        });
                    }

                    return updatedChats.sort((a, b) =>
                        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
                    );
                } else {
                    return prev;
                }
            });
        },
        (newChatId) => {
            // New Chat Created
            MessagingService.getChat(newChatId, user.id).then(newChat => {
                if (newChat) {
                    setChats(prev => {
                        if (prev.some(c => c.id === newChat.id)) return prev;
                        return [newChat, ...prev];
                    });
                }
            }).catch((error) => {
              console.error('[MessagingContext] Failed to load a new conversation:', error);
            });
        },
        // onMessageUpdated — update sidebar lastMessage preview when a message is edited
        (updatedMsg) => {
            setChats(prev => prev.map(c => {
                if (c.id === updatedMsg.chat_id && c.lastMessage?.id === updatedMsg.id) {
                    return {
                        ...c,
                        lastMessage: { ...c.lastMessage, content: updatedMsg.content, edited_at: updatedMsg.edited_at },
                    };
                }
                return c;
            }));
        },
        setRealtimeStatus,
    );

    // Sync on Focus/Online. Realtime keeps the list current while the tab is
    // open, so a focus inside FOCUS_RESYNC_GAP_MS of the last load (switching
    // between windows) does not refetch it.
    const handleFocus = () => {
      if (Date.now() - lastChatsLoadAt.current < FOCUS_RESYNC_GAP_MS) return;
      void loadChats(true);
    };
    const handleSync = () => loadChats(true);
    const handleNativeRefresh = () => loadChats(true);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleSync);
    window.addEventListener(NATIVE_REFRESH_EVENT, handleNativeRefresh as EventListener);
    
    return () => {
        clearTimeout(initialLoadTimer);
        MessagingService.unsubscribeGlobalChanges(channel);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('online', handleSync);
        window.removeEventListener(NATIVE_REFRESH_EVENT, handleNativeRefresh as EventListener);
    };
  }, [user, loadChats]);

  // ── Native chat list sync ─────────────────────────────────────────────────
  // Whenever the chat list updates, push a formatted snapshot to the native
  // shell so it can render a fully-native SwiftUI list on iPhone.
  useEffect(() => {
    if (!isNativeApp() || !user || chats.length === 0) return;

    const formatChat = (chat: Chat) => {
      const meta = getChatMetadata(chat, user) as ChatMetadata;
      const isDirect = chat.type === 'direct';
      const isProject = !!meta.projectId;
      const isCommercial = isClientTeamChat(chat);
      const type = isDirect ? 'direct' : isProject ? 'project' : isCommercial ? 'commercial' : 'group';
      const lastAt = (chat as any).lastMessage?.created_at || chat.updated_at;
      return {
        id: chat.id,
        type,
        name: meta.name,
        initials: meta.initials || '??',
        avatarURL: meta.avatar || null,
        preview: (chat as any).lastMessage?.content || null,
        timestamp: lastAt ? formatRelativeTime(lastAt) : null,
        unreadCount: chat.unreadCount || 0,
      };
    };

    // Native renders the same flat conversation list as web. The bridge still
    // uses a `sections` envelope for backwards compatibility with released
    // shells, but there is only one unlabelled section.
    const sections = [{ id: 'all', label: '', chats: chats.map(formatChat) }];

    NativeBridge.send('chatList', { sections });
  }, [chats, user]);

  const value = {
    unreadCount,
    chats,
    isLoadingChats,
    chatsUnavailable,
    realtimeStatus,
    refreshChats,
    markChatAsRead,
    deleteChat,
    hardDeleteChat,
    hideChat,
    markChatAsUnread,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
}

const MESSAGING_FALLBACK: MessagingContextType = {
  unreadCount: 0,
  chats: [],
  isLoadingChats: false,
  chatsUnavailable: false,
  realtimeStatus: 'closed',
  refreshChats: async () => {},
  markChatAsRead: async () => {},
  deleteChat: async () => false,
  hardDeleteChat: async () => false,
  hideChat: async () => false,
  markChatAsUnread: async () => false,
};

export function useMessaging() {
  const context = useContext(MessagingContext);
  return context ?? MESSAGING_FALLBACK;
}
